import { useMsal } from '@azure/msal-react';
import { refreshTokenHandler } from '@brightdrop/auth-client';
import { BDContainer } from '@brightdrop/bd-ui';
import { useTranslations } from '@brightdrop/localization-client';
import { datadogRum } from '@datadog/browser-rum';
import { Box, Container } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AlertMessage } from '~/common/components';
import { isMSALAuthError } from '~/common/utils/auth/auth.utils';

import Header from '~/features/header/Header';

const MESSAGES = {
  'common:alertMsg.messageView.serviceUnavailable.header':
    'Temporarily Unavailable',
  'common:alertMsg.messageView.serviceUnavailable.body':
    'The {{brandName}} application is not available at this time. Please try again later.',
  'common:alertMsg.messageView.serviceUnavailable.button': 'Try Again',
};

const ErrorFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}): JSX.Element => {
  const { translations } = useTranslations(MESSAGES);
  const navigate = useNavigate();
  const { instance } = useMsal();

  const onClick = () => {
    navigate('/');
    resetErrorBoundary();
  };

  const [ready, setReady] = useState(false);

  useEffect(() => {
    // force login on unexpected auth error
    if (isMSALAuthError(error) && instance) {
      refreshTokenHandler();
    }
    // TODO: [rebrand-internal] dev-facing log prefix "BrightDrop Web" — defer until a coordinated internal identifier cleanup (may affect Datadog/log filters)
    console.error('BrightDrop Web - Unexpected App Error', error);

    datadogRum.addAction('Temporarily Unavailable fallback rendered', {
      source: 'custom',
      errorBoundary: 'ErrorFallback',
      originalError: error.message,
      originalErrorType: error.name,
      viewPath: window.location.pathname,
      userFacingMessage: 'Temporarily Unavailable',
    });

    setReady(true);
  }, [error, instance]);

  return (
    <>
      <Header />
      {ready && (
        <BDContainer id="error-fallback">
          <Container maxWidth={false} className="contentContainer">
            <Box flex="1" className="contentWrapper" position="relative">
              <AlertMessage
                header={
                  translations[
                    'common:alertMsg.messageView.serviceUnavailable.header'
                  ]
                }
                body={
                  translations[
                    'common:alertMsg.messageView.serviceUnavailable.body'
                  ]
                }
                linkBtn={
                  translations[
                    'common:alertMsg.messageView.serviceUnavailable.button'
                  ]
                }
                topic="serviceUnavailable"
                id="service-unavailable"
                onClick={onClick}
              />
            </Box>
          </Container>
        </BDContainer>
      )}
    </>
  );
};
export default ErrorFallback;
